import { MontoColones } from '@/components/dominio/MontoColones';
import { MontoDolares } from '@/components/dominio/MontoDolares';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useActualizarPago, usePagosCredito, useProveedores } from '@/hooks/useFlujo';
import { cliente } from '@/lib/cliente';
import type { FiltrosPagosCredito, PagoCreditoHistorico } from '@adelante/shared';
import { formatearColones, formatearDolares, formatearRangoSemana } from '@adelante/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Download, Link2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

const TODOS = '__todos__';
const SIN_PROVEEDOR = '__sin_proveedor__';

/** Moneda con la que se paga realmente esta fila: el override del pago manda. */
const monedaEfectiva = (p: PagoCreditoHistorico) => p.monedaPago ?? p.moneda;

/**
 * "A pagar" tal como lo cuenta la hoja de Pagos Semanales: si el pago está confirmado
 * pero sin monto propio, el adeudado hace de relleno. Sin esta misma regla los subtotales
 * del acordeón no cuadrarían ni con la barra de totales ni con las líneas resumen.
 */
const aPagarEfectivo = (p: PagoCreditoHistorico) =>
  p.montoProgramado ?? (p.pagado ? (p.montoAdeudado ?? 0) : 0);

/** Campos de un pago que esta pantalla puede corregir (subconjunto de PUT /api/pagos/{id}). */
type ParcheDePago = Omit<Parameters<ReturnType<typeof useActualizarPago>['mutate']>[0], 'id'>;

// ─── Celda editable ───────────────────────────────────────────────────────────

function CeldaMonto({
  valor,
  moneda,
  onGuardar,
  destacada = false,
  derivadoDe = null,
}: {
  valor: number | null;
  moneda: 'C' | 'D';
  onGuardar: (nuevo: number | null) => void;
  destacada?: boolean;
  /**
   * Monto con el que Pagos Semanales rellena esta celda cuando el valor real es null
   * (el adeudado, en pagos ya confirmados). Se muestra en gris para distinguir el dato
   * que existe del que solo se está infiriendo; un clic lo materializa.
   */
  derivadoDe?: number | null;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');
  const Monto = moneda === 'C' ? MontoColones : MontoDolares;

  function confirmar() {
    setEditando(false);
    const limpio = texto.trim().replace(/[^\d.-]/g, '');
    const nuevo = limpio === '' ? null : Number(limpio);
    if (nuevo !== null && Number.isNaN(nuevo)) {
      toast.error('Monto inválido');
      return;
    }
    if (nuevo === valor) return;
    onGuardar(nuevo === null ? null : Math.round(nuevo * 100) / 100);
  }

  if (editando) {
    return (
      <Input
        autoFocus
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={confirmar}
        onKeyDown={(e) => {
          if (e.key === 'Enter') confirmar();
          if (e.key === 'Escape') setEditando(false);
        }}
        className="h-7 text-right text-sm tabular-nums"
      />
    );
  }

  const derivado = valor === null && derivadoDe !== null;

  return (
    <button
      type="button"
      onClick={() => {
        setTexto(valor === null ? (derivadoDe !== null ? String(derivadoDe) : '') : String(valor));
        setEditando(true);
      }}
      title={derivado ? 'Valor inferido del adeudado — clic para fijarlo' : undefined}
      className={`w-full rounded px-1 py-0.5 text-right text-sm tabular-nums hover:bg-zinc-100 ${
        destacada ? 'font-medium' : 'text-muted-foreground'
      } ${derivado ? 'italic text-zinc-400' : ''}`}
    >
      <Monto monto={derivado ? derivadoDe : valor} />
    </button>
  );
}

// ─── Fila ─────────────────────────────────────────────────────────────────────

function FilaPagoCredito({
  pago,
  onReasignar,
}: {
  pago: PagoCreditoHistorico;
  onReasignar: (pago: PagoCreditoHistorico) => void;
}) {
  const actualizar = useActualizarPago();
  const moneda = monedaEfectiva(pago);
  const [editandoNota, setEditandoNota] = useState(false);
  const [nota, setNota] = useState(pago.notas ?? '');

  const guardar = (data: ParcheDePago) =>
    actualizar.mutate(
      { id: pago.pagoId, ...data },
      { onError: (e: Error) => toast.error(e.message) },
    );

  return (
    <>
      <tr className={`border-b last:border-0 ${pago.pagado ? 'bg-green-50/40' : ''}`}>
        {/* El concepto es lo que identifica el pago: en los datos migrados desde Excel el
            nombre del proveedor quedó ahí, sin vínculo a flu.proveedores. */}
        <td className="py-1.5 pl-1 pr-2 w-[28%]">
          <div className="truncate text-sm font-medium" title={pago.conceptoNombre}>
            {pago.conceptoNombre}
          </div>
          {!pago.conceptoActivo && <div className="text-xs text-muted-foreground">archivado</div>}
        </td>
        <td className="px-2 py-1.5 w-[22%] text-sm truncate">
          {pago.proveedorNombre ? (
            <>
              <span className="text-muted-foreground">{pago.proveedorNombre}</span>
              {pago.vendorCode && (
                <span className="ml-1 text-xs text-muted-foreground/70">· {pago.vendorCode}</span>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={() => onReasignar(pago)}
              className="rounded px-1 py-0.5 text-xs text-amber-600 hover:bg-amber-50"
            >
              Vincular proveedor
            </button>
          )}
        </td>
        <td className="px-2 py-1.5 w-[6%] text-center text-sm">{moneda === 'C' ? '₡' : '$'}</td>
        <td className="border-l border-border/40 px-1 py-1.5 w-[13%]">
          <CeldaMonto
            valor={pago.montoAdeudado}
            moneda={moneda}
            onGuardar={(v) => guardar({ montoAdeudado: v })}
          />
        </td>
        <td className="border-l border-border/40 px-1 py-1.5 w-[13%]">
          <CeldaMonto
            valor={pago.montoProgramado}
            moneda={moneda}
            derivadoDe={pago.pagado ? pago.montoAdeudado : null}
            onGuardar={(v) => guardar({ montoProgramado: v })}
          />
        </td>
        <td className="border-l border-border/40 px-1 py-1.5 w-[13%]">
          <CeldaMonto
            valor={pago.montoPagado}
            moneda={moneda}
            destacada
            derivadoDe={pago.pagado ? pago.montoAdeudado : null}
            onGuardar={(v) => guardar({ montoPagado: v })}
          />
        </td>
        <td className="border-l border-border/40 px-2 py-1.5 w-[5%] text-center">
          <input
            type="checkbox"
            checked={pago.pagado}
            onChange={(e) =>
              guardar({
                pagado: e.target.checked,
                ...(e.target.checked && pago.montoPagado === null
                  ? { montoPagado: pago.montoProgramado }
                  : {}),
              })
            }
            className="h-4 w-4 accent-adelante-primary"
            aria-label={`Marcar ${pago.conceptoNombre} como pagado`}
          />
        </td>
        <td className="w-[0%] py-1.5 pl-2 pr-1 text-right whitespace-nowrap">
          <button
            type="button"
            onClick={() => setEditandoNota((v) => !v)}
            className="rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-zinc-100"
            title="Nota"
          >
            {pago.notas ? '📝' : '＋'}
          </button>
          <button
            type="button"
            onClick={() => onReasignar(pago)}
            className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-zinc-100"
            title="Cambiar el proveedor vinculado a este concepto"
          >
            <Link2 className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      {(editandoNota || pago.notas) && (
        <tr className="border-b last:border-0">
          <td colSpan={8} className="pb-2 pl-1 pr-1">
            {editandoNota ? (
              <Input
                autoFocus
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                onBlur={() => {
                  setEditandoNota(false);
                  if ((pago.notas ?? '') !== nota) guardar({ notas: nota.trim() || null });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                  if (e.key === 'Escape') {
                    setNota(pago.notas ?? '');
                    setEditandoNota(false);
                  }
                }}
                placeholder="Nota..."
                className="h-7 text-sm"
              />
            ) : (
              <span className="text-xs text-muted-foreground">{pago.notas}</span>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Diálogo de reasignación de proveedor ─────────────────────────────────────

function DialogReasignarProveedor({
  pago,
  onClose,
}: {
  pago: PagoCreditoHistorico | null;
  onClose: () => void;
}) {
  const { data: proveedores } = useProveedores();
  const qc = useQueryClient();
  // El padre remonta este componente con `key` al cambiar de fila, así que el
  // estado inicial ya refleja el proveedor vinculado hoy.
  const [seleccion, setSeleccion] = useState<string>(
    pago?.proveedorId ? String(pago.proveedorId) : '',
  );
  const [guardando, setGuardando] = useState(false);

  async function guardar() {
    if (!pago) return;
    setGuardando(true);
    try {
      const res = await cliente.conceptos.actualizar(pago.conceptoId, {
        proveedorId: seleccion ? Number(seleccion) : null,
      });
      if (res.error) throw new Error(res.error.mensaje);
      toast.success('Proveedor reasignado');
      qc.invalidateQueries({ queryKey: ['pagos-credito'] });
      qc.invalidateQueries({ queryKey: ['conceptos'] });
      qc.invalidateQueries({ queryKey: ['semana'] });
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo reasignar');
    } finally {
      setGuardando(false);
    }
  }

  return (
    // modal={false}: sin esto, el Select de abajo no puede hacer scroll con la
    // rueda del mouse (mismo conflicto de foco que VendorPickerBC dentro de un Dialog).
    <Dialog open={pago !== null} onOpenChange={(abierto) => !abierto && onClose()} modal={false}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reasignar proveedor</DialogTitle>
        </DialogHeader>
        {pago && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              El concepto <strong>{pago.conceptoNombre}</strong> está vinculado a{' '}
              <strong>{pago.proveedorNombre ?? 'ningún proveedor'}</strong>. El cambio aplica a
              todas las semanas de este concepto.
            </p>
            <div className="space-y-1.5">
              <Label>Proveedor</Label>
              <Select value={seleccion} onValueChange={setSeleccion}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar proveedor..." />
                </SelectTrigger>
                <SelectContent>
                  {(proveedores ?? []).map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.nombre}
                      {p.vendorCode ? ` · ${p.vendorCode}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={!seleccion || guardando}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Pantalla ─────────────────────────────────────────────────────────────────

/**
 * Consulta y corrección del histórico de pagos a proveedores de crédito.
 *
 * En Pagos Semanales esta categoría se muestra colapsada en dos líneas
 * ("Proveedores Crédito Colones/Dólares") porque el pago se ejecuta desde Pago
 * Proveedores. Acá se ve el detalle real: son las mismas filas de la base, así
 * que corregir un monto acá corrige también el resumen semanal y el historial.
 */
export function ProveedoresCreditoPantalla() {
  const [anio, setAnio] = useState(TODOS);
  const [semanaId, setSemanaId] = useState(TODOS);
  const [proveedorId, setProveedorId] = useState(TODOS);
  const [moneda, setMoneda] = useState(TODOS);
  const [estado, setEstado] = useState(TODOS);
  const [busqueda, setBusqueda] = useState('');
  const [reasignando, setReasignando] = useState<PagoCreditoHistorico | null>(null);

  const filtros: FiltrosPagosCredito = useMemo(
    () => ({
      anio: anio === TODOS ? null : Number(anio),
      semanaId: semanaId === TODOS ? null : Number(semanaId),
      proveedorId:
        proveedorId === TODOS || proveedorId === SIN_PROVEEDOR ? null : Number(proveedorId),
      sinProveedor: proveedorId === SIN_PROVEEDOR ? true : null,
      moneda: moneda === TODOS ? null : (moneda as 'C' | 'D'),
      estado: estado === TODOS ? null : (estado as 'pagado' | 'pendiente'),
      q: busqueda.trim() || null,
    }),
    [anio, semanaId, proveedorId, moneda, estado, busqueda],
  );

  const { data, isLoading, isError, error } = usePagosCredito(filtros);

  const anios = useMemo(
    () => [...new Set((data?.semanas ?? []).map((s) => s.anio))].sort((a, b) => b - a),
    [data?.semanas],
  );
  const semanasDelAnio = useMemo(
    () => (data?.semanas ?? []).filter((s) => anio === TODOS || s.anio === Number(anio)),
    [data?.semanas, anio],
  );

  // Agrupado por semana, preservando el orden que ya trae el backend (más reciente primero).
  const porSemana = useMemo(() => {
    const mapa = new Map<
      number,
      { titulo: string; rango: string; pagos: PagoCreditoHistorico[] }
    >();
    for (const p of data?.pagos ?? []) {
      if (!mapa.has(p.semanaId)) {
        mapa.set(p.semanaId, {
          titulo: `Semana ${p.numeroSemana} · ${p.anio}`,
          rango: formatearRangoSemana(p.fechaInicio, p.fechaFin),
          pagos: [],
        });
      }
      mapa.get(p.semanaId)?.pagos.push(p);
    }
    return [...mapa.entries()];
  }, [data?.pagos]);

  function exportarCsv() {
    const filas = data?.pagos ?? [];
    if (filas.length === 0) {
      toast.error('No hay filas que exportar');
      return;
    }
    const escapar = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [
      [
        'Año',
        'Semana',
        'Desde',
        'Hasta',
        'Concepto',
        'Proveedor vinculado',
        'VendorCode',
        'Moneda',
        'Adeudado',
        'A pagar',
        'Pagado',
        'Confirmado',
        'Notas',
      ].join(','),
      ...filas.map((p) =>
        [
          p.anio,
          p.numeroSemana,
          p.fechaInicio,
          p.fechaFin,
          escapar(p.conceptoNombre),
          escapar(p.proveedorNombre),
          escapar(p.vendorCode),
          monedaEfectiva(p),
          p.montoAdeudado ?? '',
          p.montoProgramado ?? '',
          p.montoPagado ?? '',
          p.pagado ? 'Sí' : 'No',
          escapar(p.notas),
        ].join(','),
      ),
    ].join('\r\n');

    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `proveedores-credito-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const totales = data?.totales;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-xl font-bold">Proveedores Crédito</h1>
          <p className="text-sm text-muted-foreground">
            Detalle histórico de los pagos que en Pagos Semanales se ven resumidos por moneda
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select
            value={anio}
            onValueChange={(v) => {
              setAnio(v);
              setSemanaId(TODOS);
            }}
          >
            <SelectTrigger className="w-28">
              <SelectValue placeholder="Año" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todo año</SelectItem>
              {anios.map((a) => (
                <SelectItem key={a} value={String(a)}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={semanaId} onValueChange={setSemanaId}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Semana" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todas las semanas</SelectItem>
              {semanasDelAnio.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  Semana {s.numeroSemana} · {s.anio}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={proveedorId} onValueChange={setProveedorId}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Proveedor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos los proveedores</SelectItem>
              <SelectItem value={SIN_PROVEEDOR}>Sin proveedor vinculado</SelectItem>
              {(data?.proveedores ?? []).map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>
                  {p.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={moneda} onValueChange={setMoneda}>
            <SelectTrigger className="w-28">
              <SelectValue placeholder="Moneda" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>₡ y $</SelectItem>
              <SelectItem value="C">Colones</SelectItem>
              <SelectItem value="D">Dólares</SelectItem>
            </SelectContent>
          </Select>

          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos</SelectItem>
              <SelectItem value="pagado">Pagados</SelectItem>
              <SelectItem value="pendiente">Pendientes</SelectItem>
            </SelectContent>
          </Select>

          <Input
            placeholder="Buscar proveedor o concepto..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-56"
          />

          <Button variant="outline" onClick={exportarCsv} className="gap-2">
            <Download className="h-4 w-4" />
            CSV
          </Button>
        </div>
      </div>

      {/* Totales del filtro activo */}
      {totales && (
        <div className="flex flex-wrap items-center gap-6 rounded-lg border bg-white px-4 py-3 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Filas</div>
            <div className="font-semibold tabular-nums">
              {totales.filasPagadas}/{totales.filas} pagadas
            </div>
          </div>
          {totales.filasDerivadas > 0 && (
            <div title="Pagos confirmados sin monto propio: la hoja semanal los rellena con el adeudado">
              <div className="text-xs text-muted-foreground">Por revisar</div>
              <div className="font-semibold tabular-nums text-amber-600">
                {totales.filasDerivadas}
              </div>
            </div>
          )}
          <div>
            <div className="text-xs text-muted-foreground">A pagar ₡</div>
            <div className="font-semibold tabular-nums">
              {formatearColones(totales.programadoColones)}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">A pagar $</div>
            <div className="font-semibold tabular-nums">
              {formatearDolares(totales.programadoDolares)}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Pagado ₡</div>
            <div className="font-semibold tabular-nums">
              {formatearColones(totales.pagadoColones)}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Pagado $</div>
            <div className="font-semibold tabular-nums">
              {formatearDolares(totales.pagadoDolares)}
            </div>
          </div>
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-destructive/40 bg-red-50 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : 'No se pudo cargar el histórico'}
        </div>
      )}

      {isLoading && <div className="text-sm text-muted-foreground">Cargando...</div>}

      {!isLoading && porSemana.length === 0 && (
        <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
          No hay pagos de proveedores de crédito para estos filtros.
        </div>
      )}

      {porSemana.length > 0 && (
        <Accordion
          type="multiple"
          defaultValue={porSemana.slice(0, 1).map(([id]) => String(id))}
          className="rounded-lg border bg-white px-4"
        >
          {porSemana.map(([id, grupo]) => {
            const colones = grupo.pagos
              .filter((p) => monedaEfectiva(p) === 'C')
              .reduce((a, p) => a + aPagarEfectivo(p), 0);
            const dolares = grupo.pagos
              .filter((p) => monedaEfectiva(p) === 'D')
              .reduce((a, p) => a + aPagarEfectivo(p), 0);
            return (
              <AccordionItem key={id} value={String(id)}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex flex-1 items-center gap-3 text-left">
                    <span className="text-sm font-semibold">{grupo.titulo}</span>
                    <span className="text-xs text-muted-foreground">{grupo.rango}</span>
                    <span className="text-xs text-muted-foreground">
                      {grupo.pagos.filter((p) => p.pagado).length}/{grupo.pagos.length}
                    </span>
                    <div className="ml-auto flex items-center gap-3 pr-2 text-xs tabular-nums text-muted-foreground">
                      {colones > 0 && <span>{formatearColones(colones)}</span>}
                      {dolares > 0 && <span>{formatearDolares(dolares)}</span>}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-xs text-muted-foreground">
                        <th className="pb-2 text-left font-medium">Concepto</th>
                        <th className="pb-2 text-left font-medium">Proveedor vinculado</th>
                        <th className="pb-2 text-center font-medium">Mon.</th>
                        <th className="border-l border-border/40 pb-2 pl-3 text-right font-medium">
                          Adeudado
                        </th>
                        <th className="border-l border-border/40 pb-2 pl-3 text-right font-medium">
                          A pagar
                        </th>
                        <th className="border-l border-border/40 pb-2 pl-3 text-right font-medium">
                          Pagado
                        </th>
                        <th className="border-l border-border/40 pb-2 pl-3 text-center font-medium">
                          ✓
                        </th>
                        <th className="pb-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {grupo.pagos.map((p) => (
                        <FilaPagoCredito key={p.pagoId} pago={p} onReasignar={setReasignando} />
                      ))}
                    </tbody>
                  </table>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <DialogReasignarProveedor
        key={reasignando?.conceptoId ?? 'ninguno'}
        pago={reasignando}
        onClose={() => setReasignando(null)}
      />
    </div>
  );
}
